import {
  Drawer,
  DrawerProps,
  Box,
  IconButton,
  Stack,
  SxProps,
} from "@mui/material";
import { ReactNode } from "react";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import FlexBox from "../layouts/Flexbox";
import { Theme } from "@mui/material/styles";
import { APP_COLORS } from "../../enums";

interface AppDrawerProps extends Omit<DrawerProps, "children" | "title"> {
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  mainContentSx?: SxProps<Theme>;
  onClose: () => void;
}

function AppDrawer({
  title,
  children,
  footer,
  onClose,
  anchor = "right",
  mainContentSx,
  PaperProps,
  ...drawerProps
}: AppDrawerProps) {
  const { sx: paperSx, ...restPaperProps } = PaperProps ?? {};

  return (
    <Drawer
      anchor={anchor}
      onClose={onClose}
      PaperProps={{
        ...restPaperProps,
        sx: [
          {
            height: "100%",
            maxHeight: "100vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            overscrollBehavior: "contain",
          },
          ...(paperSx != null
            ? Array.isArray(paperSx)
              ? paperSx
              : [paperSx]
            : []),
        ],
      }}
      {...drawerProps}
    >
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          height: "100%",
          overflow: "hidden",
        }}
      >
        <FlexBox
          alignItems="center"
          justifyContent="space-between"
          p={2}
          borderBottom={1}
          borderColor={APP_COLORS.greyLight3}
          sx={{ flexShrink: 0 }}
        >
          {typeof title === "string" ? (
            <Typography
              variant="heading5-helveticaNeue-medium"
              color={APP_COLORS.text}
            >
              {title}
            </Typography>
          ) : (
            title
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </FlexBox>

        <Box
          flex={1}
          minHeight={0}
          overflow="auto"
          p={2}
          sx={{
            overscrollBehavior: "contain",
            ...mainContentSx,
          }}
        >
          {children}
        </Box>

        {footer && (
          <Box
            p={2}
            borderTop={1}
            borderColor={APP_COLORS.greyLight3}
            sx={{ flexShrink: 0 }}
          >
            {footer}
          </Box>
        )}
      </Stack>
    </Drawer>
  );
}

export default AppDrawer;
